import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Email } from '@/types/email';
import type { AiAnnotation, AiSuggestedEvent, AiEventSuggestionResult } from '@/types/ai';
import { fetchMessageEventSuggestions, runMessageEventSuggestions } from '@/api/aiTasksApi';
import { ApiError } from '@/api/core/client';

export interface EventSuggestionEntry extends AiSuggestedEvent {
  id: string;
  title: string;
  startDate: string;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  location: string | null;
  description: string;
  allDay: boolean;
}

interface UseMessageEventSuggestionsResult {
  annotation: AiAnnotation | null;
  suggestions: EventSuggestionEntry[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  requestSuggestions: (options?: { force?: boolean }) => Promise<AiAnnotation | null>;
  resetError: () => void;
}

function coerceEvents(annotation: AiAnnotation | null): EventSuggestionEntry[] {
  if (!annotation?.result) {
    return [];
  }
  const result = annotation.result as unknown;
  if (!result || typeof result !== 'object') {
    return [];
  }
  const suggestionResult = result as AiEventSuggestionResult;
  if (!('events' in suggestionResult) || !Array.isArray(suggestionResult.events)) {
    return [];
  }
  const events = suggestionResult.events;
  return events
    .map<EventSuggestionEntry | null>((event, index) => {
      if (!event || typeof event !== 'object') {
        return null;
      }
      const title = typeof event.title === 'string' ? event.title.trim() : '';
      if (!title) {
        return null;
      }
      const startDate = typeof event.startDate === 'string' ? event.startDate.trim() : '';
      if (!startDate) {
        return null;
      }
      const startTime = typeof event.startTime === 'string' ? event.startTime.trim() : null;
      const endDate = typeof event.endDate === 'string' ? event.endDate.trim() : null;
      const endTime = typeof event.endTime === 'string' ? event.endTime.trim() : null;
      const location = typeof event.location === 'string' ? event.location.trim() : null;
      const description = typeof event.description === 'string' ? event.description.trim() : '';
      const allDay = event.allDay === true || (!startTime && !endTime);
      return {
        id: event.id || `ai-event-${index}`,
        title,
        startDate,
        startTime: allDay ? null : startTime,
        endDate: endDate || startDate,
        endTime: allDay ? null : endTime,
        location: location || null,
        description,
        allDay,
        confidence: event.confidence ?? null,
      };
    })
    .filter((entry): entry is EventSuggestionEntry => Boolean(entry));
}

export function useMessageEventSuggestions(email: Email | null): UseMessageEventSuggestionsResult {
  const [annotation, setAnnotation] = useState<AiAnnotation | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const emailId = email?.id ?? null;
  const accountId = email?.accountId ?? null;

  // Identity anchor for the EXPLICIT request path (X3): event suggestions
  // requested for message A must never render under message B after navigation.
  const currentEmailIdRef = useRef<string | null>(emailId);

  useEffect(() => {
    currentEmailIdRef.current = emailId;
    setAnnotation(null);
    setError(null);
    setHasFetched(false);
  }, [accountId, emailId]);

  useEffect(() => {
    if (!emailId || !accountId || hasFetched) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMessageEventSuggestions({ accountId, messageId: emailId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result) {
          setAnnotation(result);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load event suggestions';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHasFetched(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, emailId, hasFetched]);

  const requestSuggestions = useCallback(
    async (options?: { force?: boolean }) => {
      if (!accountId || !emailId) {
        return null;
      }
      const { force = false } = options || {};
      const requestEmailId = emailId;
      setError(null);
      setLoading(true);
      try {
        const result = await runMessageEventSuggestions({
          accountId,
          messageId: emailId,
          force,
          source: 'detail_view',
        });
        // Drop the response if the user navigated away mid-request (X3).
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        setAnnotation(result);
        setHasFetched(true);
        return result;
      } catch (err) {
        if (currentEmailIdRef.current !== requestEmailId) {
          return null;
        }
        if (err instanceof ApiError && err.status === 503) {
          setError('AI suggestions are temporarily unavailable');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to run event suggestions');
        }
        throw err;
      } finally {
        if (currentEmailIdRef.current === requestEmailId) {
          setLoading(false);
        }
      }
    },
    [accountId, emailId]
  );

  const suggestions = useMemo(() => coerceEvents(annotation), [annotation]);

  const resetError = useCallback(() => setError(null), []);

  return {
    annotation,
    suggestions,
    isLoading,
    error,
    hasFetched,
    requestSuggestions,
    resetError,
  };
}
